import { HEADREACH_TOP_KMH } from '../boat/headreach.ts';
import { fromDisplayKmh } from '../constants.ts';
import { closestPointOnSegment, distance, mapLapCount } from '../map/document.ts';
import type { Checkpoint, MapDocument } from '../map/types.ts';
import { dot, headingUnit } from '../math.ts';
import type { MovePath } from '../path.ts';
import { advanceRace, eligibleCheckpoints, type RaceState } from '../race.ts';
import type { TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { buildRaster, rasterClearAhead, type Raster } from './raster.ts';
import {
  buildFieldGrid,
  buildGateField,
  sampleGateField,
  type FieldGrid,
  type GateField,
  type SpeedLimits,
} from './timeField.ts';

/**
 * What a bot knows about one map, built from the document it is handed (catalog, editor clone,
 * or a redesign) the first time a bot needs it. Nothing here is authored or shipped per map.
 */
export type Course = {
  map: MapDocument;
  laps: number;
  /** Distinct numbered orders, plus each any-order gate, plus the line. */
  jobsPerLap: number;
  raster: Raster;
  models: Map<string, CourseModel>;
};

/**
 * The course under one hull's speed limits: a quickest-route field per gate and the memoised
 * rest-of-race cost from each gate.
 */
export type CourseModel = {
  course: Course;
  limits: SpeedLimits;
  grid: FieldGrid;
  fields: Map<string, GateField>;
  tails: Map<string, Tail>;
};

/** Rest of the race from a point: turns at the field's cruise, px along that route, next gate. */
export type Tail = {
  time: number;
  dist: number;
  gate: Checkpoint | null;
};

/** More open any-order gates than this and the tail only branches over the nearest few. */
const MAX_OPEN_BRANCH = 8;
const NEAREST_BRANCH = 3;

const courses = new WeakMap<MapDocument, Course>();

/** Cached per map object. The map must not be edited in place while a bot holds it. */
export function prepareCourse(map: MapDocument): Course {
  const cached = courses.get(map);

  if (cached) {
    return cached;
  }

  const orders = new Set<number>();
  let loose = 0;
  let goals = 0;

  for (const checkpoint of map.checkpoints) {
    if (checkpoint.isGoal) {
      goals = 1;
    } else if (checkpoint.order === null) {
      loose += 1;
    } else {
      orders.add(checkpoint.order);
    }
  }

  const course: Course = {
    map,
    laps: mapLapCount(map.laps),
    jobsPerLap: orders.size + loose + goals,
    raster: buildRaster(map),
    models: new Map(),
  };
  courses.set(map, course);
  return course;
}

export function speedLimits(feel: TurnFeel): SpeedLimits {
  const turnPx = feel.reach?.turn ?? feel.radius;
  const vMaxPx =
    feel.ringMode === 'planing'
      ? 2 * fromDisplayKmh(feel.planingSpeedKmh)
      : feel.ringMode === 'headreach'
        ? fromDisplayKmh(HEADREACH_TOP_KMH)
        : Infinity;

  return { turnPx, vMaxPx, vFloorPx: turnPx * 0.5 };
}

export function courseModel(course: Course, feel: TurnFeel): CourseModel {
  const limits = speedLimits(feel);
  const key = `${limits.turnPx}|${limits.vMaxPx}|${limits.vFloorPx}`;
  const cached = course.models.get(key);

  if (cached) {
    return cached;
  }

  const model: CourseModel = {
    course,
    limits,
    grid: buildFieldGrid(course.raster, limits),
    fields: new Map(),
    tails: new Map(),
  };
  course.models.set(key, model);
  return model;
}

/** Builds every gate's field now, so the first turn does not pay for them. */
export function warmCourseModel(model: CourseModel): void {
  for (const gate of model.course.map.checkpoints) {
    gateField(model, gate);
  }
}

function gateField(model: CourseModel, gate: Checkpoint): GateField {
  let field = model.fields.get(gate.id);

  if (!field) {
    field = buildGateField(model.grid, gate);
    model.fields.set(gate.id, field);
  }

  return field;
}

/** Jobs cleared so far, counting earlier laps. Monotonic over a race; the finish is the maximum. */
export function raceProgress(course: Course, race: RaceState): number {
  if (race.complete) {
    return course.laps * course.jobsPerLap;
  }

  const orders = new Set<number>();
  let loose = 0;

  for (const checkpoint of course.map.checkpoints) {
    if (checkpoint.isGoal || !race.passedCheckpointIds.has(checkpoint.id)) {
      continue;
    }

    if (checkpoint.order === null) {
      loose += 1;
    } else {
      orders.add(checkpoint.order);
    }
  }

  return (race.lap - 1) * course.jobsPerLap + orders.size + loose;
}

const raceKeys = new WeakMap<RaceState, string>();

/** Identity of a race state for memo and dedupe: lap, completion, and the passed set. */
export function raceKey(race: RaceState): string {
  let key = raceKeys.get(race);

  if (key === undefined) {
    key = `${race.lap}|${race.complete ? 1 : 0}|${[...race.passedCheckpointIds].sort().join(',')}`;
    raceKeys.set(race, key);
  }

  return key;
}

function gateMidpoint(gate: Checkpoint): Vec2 {
  return { x: (gate.a.x + gate.b.x) / 2, y: (gate.a.y + gate.b.y) / 2 };
}

/** A 4 px stroke straight through the middle of the gate, scored by the real rules. */
function crossingPath(gate: Checkpoint): MovePath {
  const mid = gateMidpoint(gate);
  const dx = gate.b.x - gate.a.x;
  const dy = gate.b.y - gate.a.y;
  const span = Math.hypot(dx, dy);
  const nx = span > 1e-6 ? -dy / span : 1;
  const ny = span > 1e-6 ? dx / span : 0;
  const from = { x: mid.x - nx * 4, y: mid.y - ny * 4 };
  const to = { x: mid.x + nx * 4, y: mid.y + ny * 4 };

  return {
    kind: 'straight',
    segments: [
      {
        p0: from,
        p1: { x: from.x + (to.x - from.x) / 3, y: from.y + (to.y - from.y) / 3 },
        p2: { x: from.x + ((to.x - from.x) * 2) / 3, y: from.y + ((to.y - from.y) * 2) / 3 },
        p3: to,
      },
    ],
  };
}

function legTo(model: CourseModel, gate: Checkpoint, point: Vec2): { time: number; dist: number } {
  const sample = sampleGateField(model.grid, gateField(model, gate), point);

  if (sample) {
    return sample;
  }

  // On land or cut off from the gate: the straight line at a crawl, so it still ranks.
  const gap = distance(point, closestPointOnSegment(point, gate.a, gate.b));
  return { time: gap / model.limits.vFloorPx, dist: gap };
}

function tailFromGate(model: CourseModel, race: RaceState, gate: Checkpoint): Tail {
  const key = `${raceKey(race)}#${gate.id}`;
  const cached = model.tails.get(key);

  if (cached) {
    return cached;
  }

  const after = advanceRace(model.course.map, race, crossingPath(gate));
  const tail = after.complete ? { time: 0, dist: 0, gate: null } : tailAt(model, after, gateMidpoint(gate));
  model.tails.set(key, tail);
  return tail;
}

/**
 * Rest of the race from `point`: the best next gate by the field plus the memoised tail beyond
 * it, so numbered alternatives, any-order gates, the line, and laps all follow `advanceRace`.
 */
export function tailAt(model: CourseModel, race: RaceState, point: Vec2): Tail {
  if (race.complete) {
    return { time: 0, dist: 0, gate: null };
  }

  let gates = eligibleCheckpoints(model.course.map, race);

  if (gates.length > MAX_OPEN_BRANCH) {
    gates = gates
      .map((gate) => ({ gate, gap: distance(point, gateMidpoint(gate)) }))
      .sort((left, right) => left.gap - right.gap)
      .slice(0, NEAREST_BRANCH)
      .map((entry) => entry.gate);
  }

  let best: Tail = { time: Infinity, dist: Infinity, gate: null };

  for (const gate of gates) {
    const leg = legTo(model, gate, point);
    const rest = tailFromGate(model, race, gate);
    const time = leg.time + rest.time;

    if (time < best.time) {
      best = { time, dist: leg.dist + rest.dist, gate };
    }
  }

  return best;
}

/** Unit direction the quickest route leaves `point` in toward `gate`, or null on a flat field. */
export function routeDirection(model: CourseModel, gate: Checkpoint, point: Vec2): Vec2 | null {
  const field = gateField(model, gate);
  const h = model.grid.cell;
  const sample = (x: number, y: number) => sampleGateField(model.grid, field, { x, y })?.dist;
  const left = sample(point.x - h, point.y);
  const right = sample(point.x + h, point.y);
  const up = sample(point.x, point.y - h);
  const down = sample(point.x, point.y + h);

  if (left !== undefined && right !== undefined && up !== undefined && down !== undefined) {
    const gx = right - left;
    const gy = down - up;
    const norm = Math.hypot(gx, gy);

    if (norm > 1e-6) {
      return { x: -gx / norm, y: -gy / norm };
    }
  }

  const target = closestPointOnSegment(point, gate.a, gate.b);
  const dx = target.x - point.x;
  const dy = target.y - point.y;
  const norm = Math.hypot(dx, dy);
  return norm > 1e-6 ? { x: dx / norm, y: dy / norm } : null;
}

/** Solves `gap = along·t + accel·t²/2` for t; heading away (`along` < 0) costs the turnaround. */
export function turnsToCover(gap: number, along: number, accel: number): number {
  if (gap <= 0) {
    return 0;
  }

  const a = Math.max(accel, 1e-6);
  return (-along + Math.sqrt(along * along + 2 * a * gap)) / a;
}

/**
 * Turns lost swinging onto the route. A click can sit at most about atan(turn / speed) off the
 * bow, and the heading turns through twice that, so a fast boat pointed away from where the
 * route goes needs several turns to come round. The first turn of swing is free: any click turns.
 */
export function swing(boat: BoatState, direction: Vec2 | null, turnPx: number): number {
  if (!direction || boat.speed <= 0) {
    return 0;
  }

  const off = Math.acos(Math.max(-1, Math.min(1, dot(headingUnit(boat.heading), direction))));
  const perTurn = 2 * Math.atan(turnPx / boat.speed);
  return Math.max(0, off / perTurn - 1);
}

/**
 * Fastest a boat braking `brakePx` a turn can be going and still stop inside `ahead` px: each
 * turn coasts its speed and sheds `brakePx`, so the run out is about v/2 + v²/2b.
 */
export function stoppableSpeed(ahead: number, brakePx: number): number {
  const b = Math.max(brakePx, 1);
  return -b / 2 + Math.sqrt((b * b) / 4 + 2 * b * Math.max(0, ahead));
}

/**
 * Turns left to the finish from this boat: the slower of the field's cruise along the quickest
 * route and what the boat's own speed and acceleration allow over that distance, plus the turns
 * it would spend braking for land dead ahead, plus the turns it needs to swing onto the route.
 * Only the way the boat is pointing and the way the route goes count: running fast beside a wall
 * is free, running fast at one, or too fast to make the next bend, is not. Those terms keep a
 * line from ending the lookahead too fast to stop or turn.
 */
export function turnsToGo(
  model: CourseModel,
  boat: BoatState,
  race: RaceState,
  accelPx: number,
  brakePx: number,
  sense: CourseSense = {},
): number {
  const tail = tailAt(model, race, boat.position);

  if (!tail.gate) {
    return tail.time;
  }

  const direction = routeDirection(model, tail.gate, boat.position);
  const along = direction ? boat.speed * dot(headingUnit(boat.heading), direction) : 0;
  // A bot that over- or underrates its brakes judges the run-out to land with that belief.
  const b = Math.max(brakePx * (sense.brakeConfidence ?? 1), 1);
  const runOut = boat.speed / 2 + (boat.speed * boat.speed) / (2 * b);
  const ahead = rasterClearAhead(
    model.course.raster,
    boat.position.x,
    boat.position.y,
    boat.heading,
    runOut + model.course.raster.cell,
  );
  const overspeed = Math.max(0, boat.speed - stoppableSpeed(ahead, b)) / b;
  const reach = turnsToCover(tail.dist, along, accelPx);

  if (sense.courseSense === 'distance') {
    // Only how far, not how twisty: no time field, no turning limit.
    return reach + overspeed;
  }

  return Math.max(tail.time, reach) + overspeed + swing(boat, direction, model.limits.turnPx);
}

/** How a bot reads the course past its lookahead (`BotLevelKnobs.courseSense`, `brakeConfidence`). */
export type CourseSense = {
  courseSense?: 'time' | 'distance';
  brakeConfidence?: number;
};
